import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { Observable } from 'rxjs';
import { MessagingCounts, MessagingNotification, MessagingThreadSummary } from './messaging.models';
import { MessagingRouteService } from './messaging-route.service';
import { MessagingStoreService } from './messaging-store.service';

@Component({
  selector: 'app-messaging-inbox-page',
  standalone: true,
  imports: [AsyncPipe, DatePipe, MatButtonModule, MatCardModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './messaging-inbox-page.component.html',
  styleUrl: './messaging-inbox-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagingInboxPageComponent implements OnInit {
  private readonly store = inject(MessagingStoreService);
  private readonly routeService = inject(MessagingRouteService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly formFieldsSelectDataService = inject(FormFieldsSelectDataService);

  readonly threads$: Observable<MessagingThreadSummary[]> = this.store.threads$;
  readonly notifications$: Observable<MessagingNotification[]> = this.store.notifications$;
  readonly counts$: Observable<MessagingCounts> = this.store.counts$;

  ngOnInit(): void {
    this.store.bootstrap();
  }

  openThread(thread: MessagingThreadSummary): void {
    if ((thread.unreadCount ?? 0) > 0) {
      this.store.markThreadRead(thread.threadId).subscribe({
        error: (error) => console.error('Failed to mark inbox-opened thread as read', error)
      });
    }

    const route = this.routeService.buildThreadRoute(thread);
    this.store.setActiveThread(thread.threadId);
    void this.router.navigate([route.routePath], { queryParams: route.queryParams });
  }

  openContext(thread: MessagingThreadSummary, event: Event): void {
    event.stopPropagation();
    const route = this.routeService.resolveContextRoute(thread);
    if (!route?.routePath) return;
    void this.router.navigate([route.routePath], { queryParams: route.queryParams });
  }

  openNotification(notification: MessagingNotification): void {
    this.store.markNotificationOpened(notification.notificationId).subscribe({
      error: (error) => console.error('Failed to mark notification opened', error)
    });
    const route = this.routeService.resolveNotificationRoute(notification);
    if (!route?.routePath) return;

    this.router.navigate([route.routePath], { queryParams: route.queryParams })
      .then((succeeded) => {
        if (succeeded) {
          this.store.markNotificationRead(notification.notificationId).subscribe({
            error: (error) => console.error('Failed to mark notification read', error)
          });
        }
      })
      .catch((err) => console.error('Navigation failed', err));
  }

  startDirectMessage(): void {
    forkJoin({
      practitioners: this.formFieldsSelectDataService.getFormFieldSelectData('practitioner', 'practitioner')
    }).subscribe((data: any) => {
      const dRef = this.dialog.open(DynamicFormsV2Component, {
        maxHeight: '90vh',
        maxWidth: '600px',
        autoFocus: false,
        data: {
          formMetaData: {
            formName: 'Start Direct Message',
            formDescription: 'Select a practitioner to start a direct conversation.',
            submitText: 'Start Conversation',
            closeDialogOnSubmit: true
          },
          formFields: [
            {
              generalProperties: {
                fieldApiName: 'practitioner',
                fieldName: 'Practitioner',
                fieldLabel: 'Select Practitioner',
                fieldType: 'IndividualReferenceField',
                isArray: false,
                isGroup: false
              },
              data: data.practitioners
            },
            {
              generalProperties: {
                fieldApiName: 'message',
                fieldName: 'Initial Message',
                fieldLabel: 'Message (optional)',
                fieldType: 'IndividualField',
                inputType: 'textarea',
                isArray: false,
                isGroup: false
              }
            }
          ]
        }
      });

      dRef.afterClosed().subscribe((result: any) => {
        const values = result?.values;
        if (!values || result?.cancelled) return;

        const selectedPractitioner = values.practitioner;
        if (!selectedPractitioner) return;

        const participantId = selectedPractitioner.split('$#$')[0].split('/')[1];
        const display = selectedPractitioner.split('$#$')[1];

        const recipient = {
          participantId,
          resourceType: 'Practitioner',
          resourceId: participantId,
          display,
          roleName: 'practitioner',
          participantType: 'practitioner'
        };

        const sender = this.store['contextService'].getCurrentParticipant();

        this.store.ensureThread({
          threadType: 'direct',
          participants: [recipient],
          title: `Direct message with ${display}`
        }).subscribe({
          next: (response) => {
            if (values.message?.trim()) {
              this.store.notifyThreadMessage(response.thread.threadId, {
                organizationId: response.thread.organizationId,
                sender,
                message: {
                  preview: values.message,
                  text: values.message,
                  sentAt: new Date().toISOString()
                }
              }).subscribe();
            }
            const route = this.routeService.buildThreadRoute(response.thread);
            void this.router.navigate([route.routePath], { queryParams: route.queryParams });
          },
          error: (error) => console.error('Failed to start direct message', error)
        });
      });
    });
  }
}
